import {action, computed, observable, reaction, runInAction} from "mobx";
import {
    GettingToPageQueryParams,
    IGettingToPageLoadDataParams,
} from "../../../types/queryParams/GettingTo";
import {IGettingToSettings, SelectedParkingStoredObject} from "../../../types/IGettingToSettings";
import {IReactionDisposer} from "mobx/lib/internal";
import languagesStore from "../../LanguagesStore/LanguagesStore";
import Api from "../../../services/api";
import buildingsStore from "../../BuildingStore/BuildingStore";
import nodesStore from "../../NodesStore/NodesStore";
import destCategoriesStore from "../../DestCategoriesStore/DestCategoriesStore";
import {UrlBuilder} from "../../../utils/urlBuilder";
import {customHistory} from "../../../Root";
import {isToday} from "../../../utils/date";
import {Routes} from "../../../routes";

class GettingToPageStore{
    @observable public queryParams:GettingToPageQueryParams = new GettingToPageQueryParams();
    @observable public settings?:IGettingToSettings|null;
    @observable public selectedParkingId:string = "";

    private readonly disposers:IReactionDisposer[] = [];

    constructor() {
        this.disposers.push(
            reaction(() => {
                return {
                    facilityLocationId: this.queryParams.facilityLocationId,
                    buildingId: this.queryParams.buildingId,
                    destinationId: this.queryParams.destinationId,
                };
            },this.loadData)
        );

        this.disposers.push(
            reaction(()=>this.queryParams.lang, (lang)=>{
                languagesStore.setSelectedIETF(lang);

                const url = UrlBuilder.Relative(Routes.GettingTo).searchParams(this.queryParams).url;
                if(location.href !== url.href)
                    customHistory.push(url.href.substring(url.origin.length));
            })
        );
    }

    dispose(){
        for(const disposer of this.disposers)
            disposer();
    }

    @action
    public setQuery(search:string){
        this.queryParams.parse(search);
    }

    @action
    public setLanguage(ietf:string){
        this.queryParams.lang = ietf;
    }

    @action
    private loadData = async (params:IGettingToPageLoadDataParams) => {
        if(!params.buildingId || !(params.facilityLocationId || params.destinationId))
        {
            this.settings = undefined;
            return;
        }

        this.selectedParkingId = this.restoreSelectedParkingId();

        try{
            const settings = await Api.getGettingToSettings(params);
            runInAction(()=>{
                this.settings = settings.shift()||null;
            });

            if(this.settings) {
                await buildingsStore.getBuildingById(params.buildingId);
                await nodesStore.getNodesByBuildingId(params.buildingId);
                await destCategoriesStore.getDestinationsByBuildingId(params.buildingId);
            }
        }
        catch(e){
            console.log(e);
        }
    }

    @action
    public setSelectedParkingId = (id:string) => {
        this.selectedParkingId = id;

        this.storeSelectedParkingId(id);
    }

    private storeSelectedParkingId(id){
        if(!this.queryParams.facilityLocationId)
            return;

        const storedObject:SelectedParkingStoredObject = {id:id,date:new Date().toDateString()};
        localStorage.setItem(this.selectedParkingIdKey, JSON.stringify(storedObject));
    }
    private restoreSelectedParkingId(){
        const item = localStorage.getItem(this.selectedParkingIdKey);
        if(!item)
            return "";

        try{
            const storedObject = JSON.parse(item) as SelectedParkingStoredObject;
            if(isToday(storedObject.date))
                return storedObject.id;

            localStorage.removeItem(this.selectedParkingIdKey);
            return "";
        }catch(e){
            console.log(e);
            return "";
        }
    }

    @computed
    private get selectedParkingIdKey(){
        return `gettingToParking_${this.queryParams.facilityLocationId}`;
    }
}

const gettingToPageStore = new GettingToPageStore();
export default gettingToPageStore;
